/**
 * CIM Add-to-cart modal
 * @description Modale affichée après un ajout au panier (pages produit, collections…) :
 *              barre de progression livraison offerte, récap du produit ajouté et cross-sell.
 *              Le contenu est rendu côté serveur par snippets/cim-atc-modal.liquid, via la
 *              Section Rendering API sur l'URL du produit ajouté (?view=cim-atc).
 *              Appelée par mm_cart.js (initAddToCartForms) après un ajout réussi.
 * @version 1.0.0
 */

const CimAtcModal = (() => {
	'use strict';

	// ==========================================================================
	// Configuration
	// ==========================================================================
	const CONFIG = {
		isDevelopment: typeof isDevelopment !== 'undefined' ? isDevelopment : false,
		selectors: {
			root: '[data-cim-atc]',
			sheet: '[data-cim-atc-sheet]',
			inner: '[data-cim-atc-inner]',
			summary: '[data-cim-atc-summary]',
			title: '[data-cim-atc-title-text]',
			titleCheck: '.cim-atc-title-check',
			progress: '[data-cim-atc-progress]',
			close: '[data-cim-atc-close]',
			viewCart: '[data-cim-atc-view-cart]',
			checkout: '[data-cim-atc-checkout]',
			track: '[data-cim-atc-track]',
			arrows: '[data-cim-atc-arrows]',
			prev: '[data-cim-atc-prev]',
			next: '[data-cim-atc-next]',
			size: '[data-cim-atc-size]',
			sizes: '[data-cim-atc-sizes]',
			sizeHint: '[data-cim-atc-size-hint]',
			variantInput: '[data-cim-atc-variant-input]',
			cardCta: '[data-cim-atc-card-cta]',
			drawerCheckout: '[data-checkout-btn]'
		},
		// Délai avant de remplir la barre : la sheet est déjà bien montée
		progressDelay: 450,
		// Durée pendant laquelle les animations d'entrée restent actives
		enteringDuration: 1800,
		addedStateDuration: 2200,
		overlaysToHide: ['shopify-chat', 'axeptio_overlay']
	};

	const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

	let currentRef = null;
	let triggerElement = null;
	let enteringTimer = null;

	// ==========================================================================
	// Logging
	// ==========================================================================
	const log = (level, message, data = {}) => {
		if (!CONFIG.isDevelopment && level === 'info') return;
		const logFn = console[level] || console.log;
		logFn(`[CimAtcModal] ${message}`, data);
	};

	const getRoot = () => document.querySelector(CONFIG.selectors.root);
	const getCartSectionId = () => (document.querySelector('.mm-cart')?.id || '').replace('shopify-section-', '');
	const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	// ==========================================================================
	// Rendu serveur
	// ==========================================================================
	/**
	 * Récupère le contenu de la modale pour un produit/variante, dans l'état actuel du panier.
	 */
	const fetchInner = async (ref) => {
		const sectionId = getCartSectionId();
		if (!sectionId) throw new Error('Cart section not found');

		const root = window.Shopify?.routes?.root || '/';
		const url = `${root}products/${encodeURIComponent(ref.handle)}?variant=${ref.variantId}&view=cim-atc&sections=${sectionId}`;
		const response = await fetch(url, { credentials: 'same-origin' });
		if (!response.ok) throw new Error(`HTTP ${response.status}`);

		const data = await response.json();
		const html = data?.[sectionId];
		if (!html) throw new Error('Empty section render');

		return new DOMParser().parseFromString(html, 'text/html').querySelector(CONFIG.selectors.inner);
	};

	// ==========================================================================
	// Barre de progression
	// ==========================================================================
	/**
	 * Remplit les barres depuis `fromWidths` (ou 0%) vers leur largeur cible inline.
	 */
	const prepareProgressFills = (container, fromWidths = []) => {
		container.querySelectorAll('.mm-progress-bar__fill').forEach((fill, i) => {
			fill.dataset.targetWidth = fill.style.width;
			fill.style.width = fromWidths[i] || '0%';
		});
	};

	const playProgressFills = (container) => {
		container.querySelectorAll('.mm-progress-bar__fill').forEach((fill) => {
			void fill.offsetWidth;
			requestAnimationFrame(() => {
				fill.style.width = fill.dataset.targetWidth || fill.style.width;
			});
		});
	};

	const popCheckIfComplete = (container, force = false) => {
		// _checkPopPending : flag posé par mm_cart.js quand le palier vient d'être franchi
		const justCompleted = force || (typeof _checkPopPending !== 'undefined' && _checkPopPending);
		if (!justCompleted) return;
		container.querySelectorAll('.mm-progress-bar--complete').forEach((bar) => {
			bar.classList.remove('is-check-popping');
			void bar.offsetWidth;
			bar.classList.add('is-check-popping');
		});
	};

	// ==========================================================================
	// Titre « Ajouté à votre panier » : vague de lettres (même effet que le
	// survol des boutons data-button-animate-chars, cf. btn_animate.js)
	// ==========================================================================
	const splitTitle = (container) => {
		const title = container.querySelector(CONFIG.selectors.title);
		if (!title || title.dataset.split) return;
		const text = title.textContent.trim();

		const srText = document.createElement('span');
		srText.className = 'cim-atc-sr-only';
		srText.textContent = text;

		const chars = document.createElement('span');
		chars.className = 'cim-atc-title-chars';
		chars.setAttribute('aria-hidden', 'true');
		[...text].forEach((char, i) => {
			const span = document.createElement('span');
			span.textContent = char === ' ' ? '\u00A0' : char;
			span.style.setProperty('--cim-atc-char-i', i);
			chars.appendChild(span);
		});

		title.replaceChildren(srText, chars);
		title.dataset.split = 'true';
	};

	const replayAnimation = (el, className) => {
		if (!el) return;
		el.classList.remove(className);
		void el.offsetWidth;
		el.classList.add(className);
		el.addEventListener('animationend', function handler(e) {
			// On attend la fin de la dernière animation enfant avant de nettoyer
			if (e.target !== el && e.target !== el.querySelector('.cim-atc-title-chars > :last-child')) return;
			el.classList.remove(className);
			el.removeEventListener('animationend', handler);
		});
	};

	const rollTitle = (root) => {
		if (prefersReducedMotion()) return;
		replayAnimation(root.querySelector(CONFIG.selectors.title), 'is-rolling');
		replayAnimation(root.querySelector(CONFIG.selectors.titleCheck), 'is-popping');
	};

	// ==========================================================================
	// Carrousel des recos
	// ==========================================================================
	const updateArrows = (root) => {
		const track = root.querySelector(CONFIG.selectors.track);
		const arrows = root.querySelector(CONFIG.selectors.arrows);
		if (!track || !arrows) return;

		const maxScroll = track.scrollWidth - track.clientWidth;
		arrows.classList.toggle('is-hidden', maxScroll <= 2);
		const prev = arrows.querySelector(CONFIG.selectors.prev);
		const next = arrows.querySelector(CONFIG.selectors.next);
		if (prev) prev.disabled = track.scrollLeft <= 2;
		if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
	};

	const scrollTrack = (root, direction) => {
		const track = root.querySelector(CONFIG.selectors.track);
		if (!track) return;
		const card = track.firstElementChild;
		const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
		const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
		// Avance d'autant de cards que visibles, pour garder un alignement propre
		const visible = Math.max(1, Math.floor((track.clientWidth + gap) / step));
		track.scrollBy({ left: direction * step * visible, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
	};

	// ==========================================================================
	// Ouverture / fermeture
	// ==========================================================================
	const handleKeydown = (e) => {
		const root = getRoot();
		if (!root?.classList.contains('is-open')) return;

		if (e.key === 'Escape' || e.key === 'Esc') {
			e.preventDefault();
			close();
			return;
		}
		if (e.key !== 'Tab') return;

		const sheet = root.querySelector(CONFIG.selectors.sheet);
		const focusables = Array.from(sheet.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
		if (!focusables.length) return;
		const first = focusables[0];
		const last = focusables[focusables.length - 1];

		if (e.shiftKey && (document.activeElement === first || document.activeElement === sheet)) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	const render = (root, inner) => {
		const sheet = root.querySelector(CONFIG.selectors.sheet);
		sheet.replaceChildren(inner);

		// Le bouton du panier latéral porte les codes promo (mm_cart.js) : on reprend son URL
		const drawerCheckout = document.querySelector(CONFIG.selectors.drawerCheckout);
		const checkout = inner.querySelector(CONFIG.selectors.checkout);
		if (drawerCheckout?.href && checkout) checkout.href = drawerCheckout.href;

		const track = inner.querySelector(CONFIG.selectors.track);
		if (track) track.addEventListener('scroll', () => updateArrows(root), { passive: true });

		prepareProgressFills(inner);
		splitTitle(inner);

		// Branche les formulaires des cards sur le flux AJAX de mm_cart.js
		if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();
		// Animation des lettres au survol du bouton « Commander »
		if (typeof window.initButtonCharacterStagger === 'function') window.initButtonCharacterStagger();
	};

	const open = (root) => {
		const sheet = root.querySelector(CONFIG.selectors.sheet);
		const html = document.documentElement;

		clearTimeout(enteringTimer);
		root.classList.add('is-entering');
		void root.offsetWidth;
		root.classList.add('is-open');
		root.setAttribute('aria-hidden', 'false');
		html.classList.add('stop-scrolling');

		CONFIG.overlaysToHide.forEach((id) => {
			const el = document.getElementById(id);
			if (el) el.style.display = 'none';
		});

		document.addEventListener('keydown', handleKeydown);
		requestAnimationFrame(() => {
			sheet?.focus({ preventScroll: true });
			updateArrows(root);
		});

		setTimeout(() => {
			playProgressFills(root);
			popCheckIfComplete(root);
		}, prefersReducedMotion() ? 0 : CONFIG.progressDelay);

		enteringTimer = setTimeout(() => root.classList.remove('is-entering'), CONFIG.enteringDuration);

		document.dispatchEvent(new CustomEvent('cim-atc:opened', { detail: { ...currentRef } }));
		log('info', 'Opened', currentRef);
	};

	const close = ({ restoreFocus = true } = {}) => {
		const root = getRoot();
		if (!root?.classList.contains('is-open')) return;

		root.classList.remove('is-open', 'is-entering');
		root.setAttribute('aria-hidden', 'true');
		document.documentElement.classList.remove('stop-scrolling');
		document.removeEventListener('keydown', handleKeydown);

		CONFIG.overlaysToHide.forEach((id) => {
			const el = document.getElementById(id);
			if (el) el.style.display = '';
		});

		if (restoreFocus && triggerElement?.isConnected) triggerElement.focus({ preventScroll: true });
		triggerElement = null;

		document.dispatchEvent(new CustomEvent('cim-atc:closed'));
	};

	// ==========================================================================
	// Ajouts au panier
	// ==========================================================================
	/**
	 * Ajout depuis une card de la modale :
	 * 1. le bouton passe tout de suite en « ✓ Ajouté » (l'ajout est déjà confirmé)
	 * 2. en arrière-plan, le récap affiche le produit ajouté, la barre et le
	 *    sous-total se mettent à jour, le titre rejoue son animation.
	 * Les recos restent en place.
	 */
	const showAddedState = (cta) => {
		if (!cta) return;
		cta.classList.remove('mm-loading', 'is-size-required');
		cta.disabled = false;
		clearTimeout(cta._cimAtcAddedTimer);
		cta.classList.add('is-added');
		cta._cimAtcAddedTimer = setTimeout(() => cta.classList.remove('is-added'), CONFIG.addedStateDuration);
	};

	const refreshAfterInnerAdd = async (root, ref) => {
		try {
			const fresh = await fetchInner(ref);
			if (!fresh) return;
			currentRef = ref;

			const oldSummary = root.querySelector(CONFIG.selectors.summary);
			const newSummary = fresh.querySelector(CONFIG.selectors.summary);
			if (oldSummary && newSummary) {
				newSummary.classList.add('is-updated');
				oldSummary.replaceWith(newSummary);
			}

			const oldProgress = root.querySelector(CONFIG.selectors.progress);
			const newProgress = fresh.querySelector(CONFIG.selectors.progress);
			if (oldProgress && newProgress) {
				const wasComplete = !!oldProgress.querySelector('.mm-progress-bar--complete');
				const fromWidths = Array.from(oldProgress.querySelectorAll('.mm-progress-bar__fill')).map((f) => f.style.width);
				prepareProgressFills(newProgress, fromWidths);
				oldProgress.replaceWith(newProgress);
				playProgressFills(newProgress);
				if (!wasComplete) popCheckIfComplete(newProgress, !!newProgress.querySelector('.mm-progress-bar--complete'));
			}

			rollTitle(root);
		} catch (error) {
			log('warn', 'Refresh after inner add failed', { error: error.message });
		}
	};

	const onInnerAdd = (form, response) => {
		const root = getRoot();
		showAddedState(form.querySelector(CONFIG.selectors.cardCta));
		if (!root) return;

		const line = Array.isArray(response?.items) ? response.items[0] : response;
		const ref = line?.handle && line?.variant_id
			? { handle: line.handle, variantId: line.variant_id }
			: currentRef;
		if (ref) refreshAfterInnerAdd(root, ref);
	};

	/**
	 * Point d'entrée appelé par mm_cart.js après un ajout réussi.
	 * @returns {Promise<boolean>} false → mm_cart.js ouvre le panier latéral (fallback)
	 */
	const onAdded = async ({ form, response } = {}) => {
		const root = getRoot();
		if (!root) return false;

		// cart/add.js ne renvoie pas item_count : on resynchronise la pastille du header
		if (typeof window.mmGetCart === 'function') {
			window.mmGetCart().catch((error) => log('warn', 'Cart count sync failed', { error: error.message }));
		}

		if (form?.closest(CONFIG.selectors.root)) {
			onInnerAdd(form, response);
			return true;
		}

		const line = Array.isArray(response?.items) ? response.items[0] : response;
		if (!line?.handle || !line?.variant_id) return false;

		try {
			const ref = { handle: line.handle, variantId: line.variant_id };
			const inner = await fetchInner(ref);
			if (!inner) return false;

			currentRef = ref;
			triggerElement = form?.querySelector('[type="submit"]') || document.activeElement;
			render(root, inner);
			open(root);
			return true;
		} catch (error) {
			log('warn', 'Modal render failed, falling back to cart drawer', { error: error.message });
			return false;
		}
	};

	// ==========================================================================
	// Interactions (délégation)
	// ==========================================================================
	const selectSize = (button) => {
		const form = button.closest('form');
		if (!form || button.disabled) return;

		form.querySelectorAll(CONFIG.selectors.size).forEach((el) => {
			const selected = el === button;
			el.classList.toggle('is-selected', selected);
			el.setAttribute('aria-pressed', String(selected));
		});
		const input = form.querySelector(CONFIG.selectors.variantInput);
		if (input) input.value = button.dataset.variantId;

		form.querySelector(CONFIG.selectors.sizes)?.classList.remove('is-size-required');
		form.querySelector(CONFIG.selectors.cardCta)?.classList.remove('is-size-required');
		const hint = form.querySelector(CONFIG.selectors.sizeHint);
		if (hint) hint.textContent = '';
	};

	const handleClick = (e) => {
		const root = getRoot();
		if (!root || !e.target.closest(CONFIG.selectors.root)) return;

		const size = e.target.closest(CONFIG.selectors.size);
		if (size) {
			selectSize(size);
			return;
		}
		if (e.target.closest(CONFIG.selectors.close)) {
			e.preventDefault();
			close();
			return;
		}
		if (e.target.closest(CONFIG.selectors.viewCart)) {
			e.preventDefault();
			close({ restoreFocus: false });
			if (typeof window.mmOpenCart === 'function') window.mmOpenCart();
			return;
		}
		const checkout = e.target.closest(CONFIG.selectors.checkout);
		if (checkout) {
			checkout.classList.add('mm-loading');
			return;
		}
		if (e.target.closest(CONFIG.selectors.prev)) scrollTrack(root, -1);
		if (e.target.closest(CONFIG.selectors.next)) scrollTrack(root, 1);
	};

	/**
	 * Phase de capture : bloque l'ajout d'une card sans taille choisie, avant
	 * que le handler de mm_cart.js ne reçoive l'événement.
	 */
	const handleSubmitCapture = (e) => {
		const form = e.target;
		if (!(form instanceof HTMLFormElement) || !form.closest(CONFIG.selectors.root)) return;

		const input = form.querySelector(CONFIG.selectors.variantInput);
		if (!input || input.value) return;

		e.preventDefault();
		e.stopImmediatePropagation();

		const sizes = form.querySelector(CONFIG.selectors.sizes);
		if (sizes) {
			sizes.classList.remove('is-size-required');
			void sizes.offsetWidth;
			sizes.classList.add('is-size-required');
		}
		// Message annoncé aux lecteurs d'écran ; visuellement, le libellé du bouton change
		// (pas de ligne ajoutée → la sheet ne bouge pas)
		const hint = form.querySelector(CONFIG.selectors.sizeHint);
		const cta = form.querySelector(CONFIG.selectors.cardCta);
		if (hint && cta) hint.textContent = cta.dataset.sizeRequiredText || '';
		cta?.classList.add('is-size-required');
	};

	// ==========================================================================
	// Init
	// ==========================================================================
	const init = () => {
		document.addEventListener('click', handleClick);
		document.addEventListener('submit', handleSubmitCapture, true);
		window.addEventListener('resize', () => {
			const root = getRoot();
			if (root?.classList.contains('is-open')) updateArrows(root);
		}, { passive: true });
		log('info', 'Initialized');
	};

	// ==========================================================================
	// Public API
	// ==========================================================================
	return { init, onAdded, close };
})();

window.CimAtcModal = CimAtcModal;

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', CimAtcModal.init);
} else {
	CimAtcModal.init();
}
